import qc from 'qc'
import gameIframe from '@ps/bridge/iframe/iframe'
import {
  getEraseNodeByChildNode,
  getEraseNodeInScene,
  getSceneNodeByNode,
  isEraseBrushCategory,
  isEraseBrushImages,
  isEraseCustomJudgeAreas,
  isEraseCustomJudgeAreasNode,
  isEraseNode,
} from '@ps/bridge/qc/base'
import { scripts } from '@ps/bridge/qc/scripts'
import generateId from '@com/utils/generate-id'
import i18n from '@ps/lang'
import _ from 'lodash'
import { AstConditionItem } from '@ps/bridge/qc/scripts/editor'
import store from '@ps/store'
import { getShapeByNode } from '@ps/store/workbench/stage/utils/helper'
import nodePool from '@ps/bridge/qc/node-pool'
import { removeUnSplitProInLayout } from '@ps/bridge/qc/node/layout'

// 擦除节点的类型
export enum EraseType {
  MASK = 0, // 蒙层
  CUSTOM_LAYER = 1, // 自定义图层
  CUSTOM_JUDGE_AREA = 2, // 自定义判定区域
  BOTTOM_IMAGE = 3, // 底图
}
// 笔触样式
export enum EraseBrushCategory {
  ROUND = 1,
  RECT = 2,
  PIC = 3,
}
// 触发时机
export enum TriggerWhen {
  UP_JUDGE = 1, // 抬手判定
  REAL_TIME_JUDGE = 2, // 实时判定
}

export type eventItem = {
  id: string
  name: string
  nameEn: string
  area: string[]
  step: number[]
}

export const drawCircle = (node: qc.Node) => {
  const bitmapData = gameIframe.qcGame.phaser.make.bitmapData(
    node.width,
    node.height,
  )
  const context = bitmapData.context
  // 设置圆的属性
  const centerX = node.width / 2 // 圆心的x坐标
  const centerY = node.height / 2 // 圆心的y坐标
  const radius = node.height / 2 // 圆的半径
  const color = '#f3ad02' // 圆的颜色

  // 绘制圆
  context.beginPath()
  context.arc(centerX, centerY, radius, 0, 2 * Math.PI, false) // 使用arc方法绘制圆
  context.fillStyle = color // 设置圆的填充颜色，此处为透明
  context.fill() // 填充圆的内部
  node.phaser.loadTexture(bitmapData)
}

export const drawRect = (node: qc.Node, config: any) => {
  const bitmapData = gameIframe.qcGame.phaser.make.bitmapData(
    node.width,
    node.height,
  )
  const context = bitmapData.context
  // 设置矩形的属性
  const x = 0 // 矩形左上角的x坐标
  const y = 0 // 矩形左上角的y坐标
  const width = node.width // 矩形的宽度
  const height = node.height // 矩形的高度
  const color = config && config.color ? config.color : '#f3ad02' // 矩形的颜色

  // 绘制矩形
  context.fillStyle = color // 设置矩形的填充颜色
  context.fillRect(x, y, width, height) // 使用fillRect方法绘制矩形
  node.phaser.loadTexture(bitmapData)
}
// 获取自定义判定区域最外层的节点
export const getCustomJudgeAreaGroup = (sceneNode: qc.Node) => {
  const eraseNode = getEraseNodeInScene(sceneNode)
  if (!eraseNode) return null
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  const customJudgeAreas = eraseComponent.customJudgeAreas
  return customJudgeAreas
}
// 获取自定义判定区域里的各个组
export const getCustomJudgeAreas = (sceneNode: qc.Node) => {
  const customJudgeAreaGroup = getCustomJudgeAreaGroup(sceneNode)
  if (!customJudgeAreaGroup) return []
  return customJudgeAreaGroup.children
}
// 获取可擦除的自定义图层
export const getEraseableEnableCustomLayerGroup = (sceneNode: qc.Node) => {
  const eraseNode = getEraseNodeInScene(sceneNode)
  if (!eraseNode) return []
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  const customLayerGroup = eraseComponent.customLayerGroup
  const layerArr = customLayerGroup.filter((node: qc.Node) => {
    const eraseGroupComponent = node.getScript(scripts.eraseGroup.name)
    return eraseGroupComponent.canErase
  })
  return layerArr
}
// 获取可擦除蒙层里的所有子节点
export const getEnableEraseableMaskLayers = (sceneNode: qc.Node) => {
  const eraseNode = getEraseNodeInScene(sceneNode)
  if (!eraseNode) return []
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  const maskImages = eraseComponent.maskImages
  return maskImages.children
}

// 获取擦除的自定义判定事件
export const getEraseCustomEvents = (node: qc.Node) => {
  const eraseNode = getEraseNodeByChildNode(node)
  if (!eraseNode) return null
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  const customEvents = Object.values(eraseComponent.customEvents)
  return customEvents
}
// 删除节点联动自定义判定事件的区域删除
export const removeNodeBindEraseCustomEvents = (nodes: qc.Node[]) => {
  const node: qc.Node = nodes[0] as qc.Node
  const eraseNode = getEraseNodeByChildNode(node)
  if (!eraseNode) return
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  for (const i in eraseComponent.customEvents) {
    const index = eraseComponent.customEvents[i].area.indexOf(node.uuid)
    if (index > -1) {
      eraseComponent.customEvents[i].area.splice(index, 1)
    }
    // 删除自定义组时需要把其子节点联动删除数据
    if (node.children.length > 0 && node.getScript(scripts.eraseGroup.name)) {
      node.children.forEach((child) => {
        const index = eraseComponent.customEvents[i].area.indexOf(child.uuid)
        if (index > -1) {
          eraseComponent.customEvents[i].area.splice(index, 1)
        }
      })
    }
  }
}

// 删除节点联动自定义判定事件的区域删除
export const recoverNodeBindCustomEvents = (
  node: qc.Node,
  oldCustomEvents: eventItem[],
) => {
  if (!oldCustomEvents) return
  const eraseNode = getEraseNodeByChildNode(node)
  if (!eraseNode) return
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  eraseComponent.customEvents = oldCustomEvents
}
// 调整节点层级顺序时联动自定义判定事件的区域数据
export const updateEraseCustomEventsAreaWhenNodeChange = (node: qc.Node) => {
  const eraseNode = getEraseNodeByChildNode(node)
  if (!eraseNode) return
  const eraseGroupComponent = node.parent.getScript(scripts.eraseGroup.name)
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  // 节点从可擦除的组挪到不可擦除组才需要删除该节点
  if (!eraseGroupComponent.canErase) {
    for (const i in eraseComponent.customEvents) {
      eraseComponent.customEvents[i].area = eraseComponent.customEvents[
        i
      ].area.filter((area: string) => area !== node.uuid)
    }
  }
}

const makeSureArray = (data) => {
  return Object.values(data)
}
export const addEraseCustomEvent = (sceneNode: qc.Node) => {
  const eraseNode = getEraseNodeInScene(sceneNode)
  if (!eraseNode) return
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  eraseComponent.customEvents = makeSureArray(eraseComponent.customEvents)
  const index = eraseComponent.customEvents.length + 1
  const customEvent = {
    id: generateId(),
    name: i18n.t('workbench.erase.event.title', 'zh-cn') + index,
    nameEn: i18n.t('workbench.erase.event.title', 'en') + index,
    area: [],
    step: [],
  }
  eraseComponent.customEvents.push(customEvent)
}

export const updateEraseCustomEventAreas = (
  sceneNode: qc.Node,
  id: string,
  area: string[],
) => {
  const eraseNode = getEraseNodeInScene(sceneNode)
  if (!eraseNode) return
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  eraseComponent.customEvents = makeSureArray(eraseComponent.customEvents)
  const eventItem = eraseComponent.customEvents.find(
    (event: eventItem) => event.id === id,
  )
  if (eventItem) {
    eventItem.area = area
  }
}

export const copyEraseCustomEvent = (sceneNode: qc.Node, id: string) => {
  const eraseNode = getEraseNodeInScene(sceneNode)
  if (!eraseNode) return
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  eraseComponent.customEvents = makeSureArray(eraseComponent.customEvents)
  const eventItem = eraseComponent.customEvents.find(
    (event: eventItem) => event.id === id,
  )
  const cloneItem = _.cloneDeep(eventItem)
  cloneItem.id = generateId()
  cloneItem.name = cloneItem.name + '_Copy'
  cloneItem.nameEn = cloneItem.nameEn + '_Copy'
  eraseComponent.customEvents.push(cloneItem)
}

export const deleteEraseCustomEvent = (sceneNode: qc.Node, id: string) => {
  const eraseNode = getEraseNodeInScene(sceneNode)
  if (!eraseNode) return
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  eraseComponent.customEvents = makeSureArray(eraseComponent.customEvents)
  const index = eraseComponent.customEvents.findIndex(
    (event: eventItem) => event.id === id,
  )
  eraseComponent.customEvents.splice(index, 1)
  const vpAst = sceneNode.getScript('playsmart.editor.data').vpAst
  for (const i in vpAst) {
    vpAst[i].condition.forEach((condition: AstConditionItem) => {
      if (condition.erase && condition.erase.customEventId === id) {
        condition.erase.customEventId = null
        condition.erase.step = null
      }
    })
  }
}

export const addEraseStep = (sceneNode: qc.Node, id: string, step: number) => {
  const eraseNode = getEraseNodeInScene(sceneNode)
  if (!eraseNode) return
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  eraseComponent.customEvents = makeSureArray(eraseComponent.customEvents)
  const eventItem = eraseComponent.customEvents.find(
    (event: eventItem) => event.id === id,
  )
  if (!eventItem.step) {
    eventItem.step = []
  }
  eventItem.step.push(step)
}

export const deleteEraseStep = (
  sceneNode: qc.Node,
  id: string,
  index: number,
) => {
  const eraseNode = getEraseNodeInScene(sceneNode)
  if (!eraseNode) return
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  eraseComponent.customEvents = makeSureArray(eraseComponent.customEvents)
  const eventItem = eraseComponent.customEvents.find(
    (event: eventItem) => event.id === id,
  )
  eventItem.step.splice(index, 1)
  const vpAst = sceneNode.getScript('playsmart.editor.data').vpAst
  for (const i in vpAst) {
    vpAst[i].condition.forEach((condition: AstConditionItem) => {
      if (
        condition.erase &&
        condition.erase.customEventId === id &&
        Number(condition.erase.step) === index
      ) {
        condition.erase.step = null
      }
    })
  }
}

export const updateEraseStep = (
  sceneNode: qc.Node,
  id: string,
  index: number,
  value: number,
) => {
  const eraseNode = getEraseNodeInScene(sceneNode)
  if (!eraseNode) return
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  eraseComponent.customEvents = makeSureArray(eraseComponent.customEvents)
  const eventItem = eraseComponent.customEvents.find(
    (event: eventItem) => event.id === id,
  )
  eventItem.step[index] = value
}

// 还原擦除组件里的子节点初始的显隐状态
export const eraseToInit = (nodes: qc.Node[]) => {
  nodes.forEach((node) => {
    if (isEraseNode(node)) {
      const eraseComponent = node.getScript(scripts.erase.name)
      eraseComponent.brushCategory.visible = false
      eraseComponent.brushImages.visible = false
      eraseComponent.customJudgeAreas.visible = false
      eraseComponent.customJudgeAreas.children.forEach((child: qc.Node) => {
        child.visible = false
        removeUnSplitProInLayout(child)
      })
      // 很奇怪，不知道哪里设置了layout里的visible，所以每次初始化游戏后都显示这些隐藏节点
      node.children.forEach((child) => {
        removeUnSplitProInLayout(child)
      })
    }
  })
  // 保存时，当前节点如果是笔触跟手图片和自定义判定区域，则隐藏后需要激活擦除组件为activeNode
  const activeNode = store.state.workspace.game.activeNode
  if (!activeNode) return
  const node = nodePool.find(activeNode.uuid)
  if (
    isEraseBrushCategory(node.parent) ||
    isEraseBrushImages(node.parent) ||
    isEraseCustomJudgeAreasNode(node.parent) ||
    isEraseCustomJudgeAreasNode(node) ||
    isEraseCustomJudgeAreas(node)
  ) {
    const eraseNode = getEraseNodeByChildNode(node)
    store.dispatch('workspace/game/updateActiveNode', {
      node: eraseNode,
      record: false,
    })
  }
}
// 擦除组件屏蔽group级的子节点的画布操作
export const eraseCanvasHandler = (node: qc.Node) => {
  // 也要延迟一阵，不知道哪里又把shape的visible设为true了
  setTimeout(() => {
    const eraseNode = getEraseNodeByChildNode(node)
    if (!eraseNode) return
    const eraseComponent = eraseNode?.getScript(scripts.erase.name)
    let shape = getShapeByNode(eraseNode)
    if (shape) {
      shape.visible(false)
    }
    shape = getShapeByNode(eraseComponent.brushImages)
    if (shape) {
      shape.visible(false)
    }
    shape = getShapeByNode(eraseComponent.brushCategory)
    if (shape) {
      shape.visible(false)
    }
    shape = getShapeByNode(eraseComponent.bottomImages)
    if (shape) {
      shape.visible(false)
    }
    shape = getShapeByNode(eraseComponent.maskImages)
    if (shape) {
      shape.visible(false)
    }
    eraseComponent.customLayerGroup.forEach((item: qc.Node) => {
      shape = getShapeByNode(item)
      if (shape) {
        shape.visible(false)
      }
    })
    shape = getShapeByNode(eraseComponent.customJudgeAreas)
    if (shape) {
      shape.visible(false)
    }
    eraseComponent.customJudgeAreas.children.forEach((item: qc.Node) => {
      shape = getShapeByNode(item)
      if (shape) {
        shape.visible(false)
      }
    })
    const sceneNode = getSceneNodeByNode(node)
    if (sceneNode) {
      shape = getShapeByNode(sceneNode)
      if (shape) {
        shape.visible(false)
      }
    }
  }, 20)
}

export const updateEraseBrushRound = (node: qc.Node) => {
  const roundNode = node
  const min = Math.min(roundNode.width, roundNode.height)
  console.log(min)
  store.commit(`workspace/game/setWidth`, {
    value: min,
    node,
  })
  store.commit(`workspace/game/setHeight`, {
    value: min,
    node,
  })
}
// 跟手锚点定位
export const updateBrushPos = (eraseNode: qc.Node, activeNode?: qc.Node) => {
  let tnode
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  if (activeNode) {
    tnode = activeNode
  } else {
    tnode = eraseComponent.brushImageSelected
  }
  if (!tnode) return
  const eraseHandImage = tnode.getScript(scripts.eraseHandImage.name)
  if (!eraseHandImage) return
  const px = eraseHandImage.brushPivot.x
  const py = eraseHandImage.brushPivot.y
  let node = null
  if (eraseComponent.brushSelected === EraseBrushCategory.ROUND) {
    node = eraseComponent.brushCategory.getChild('round')
  } else if (eraseComponent.brushSelected === EraseBrushCategory.RECT) {
    node = eraseComponent.brushCategory.getChild('rect')
  } else if (eraseComponent.brushSelected === EraseBrushCategory.PIC) {
    node = eraseComponent.brushCategory.getChild('pic')
  }
  const brushImageSelected = eraseComponent.brushImageSelected
  const localX =
    (px - brushImageSelected.pivotX) *
    brushImageSelected.width *
    brushImageSelected.scaleX
  const localY =
    (py - brushImageSelected.pivotY) *
    brushImageSelected.height *
    brushImageSelected.scaleY
  const globalPos = brushImageSelected.toGlobal(
    new gameIframe.qc.Point(localX, localY),
  )
  const localPos = eraseComponent.brushCategory.toLocal(
    new gameIframe.qc.Point(globalPos.x, globalPos.y),
  )
  node.x = localPos.x
  node.y = localPos.y
  if (eraseComponent.brushSelected === EraseBrushCategory.ROUND) {
    drawCircle(node)
  } else if (eraseComponent.brushSelected === EraseBrushCategory.RECT) {
    drawRect(node)
  }
  node.visible = true
  node.parent.visible = true
}

export const hasBrushImages = (node: qc.Node) => {
  const eraseNode = getEraseNodeByChildNode(node)
  if (!eraseNode) return false
  const eraseComponent = eraseNode.getScript(scripts.erase.name)
  return eraseComponent.brushImages.children.length > 0
}
